import React from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  TextField,
  Divider,
  CssBaseline,
  ThemeProvider,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import bannerImage from "../../assets/banner.jpeg";
import { Formik, Form, useField } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { showAppMessageToast } from "../../utils/toastUtils";
import { useDispatch } from "react-redux";
import { forgotPassword } from "../../features/auth/authSlice";

// This file is a self-contained React application that recreates the Forgot Password page
// using Material-UI components, following the same responsive layout and design
// principles as the login and register pages. All external dependencies are mocked to
// make this a runnable, single-file example.

// MOCKING EXTERNAL DEPENDENCIES FOR A STANDALONE EXAMPLE
// const useNavigate = () => (path) => console.log(`Navigating to: ${path}`);
// const showAppMessageToast = ({ type, title, message }) => console.log(`${type}: ${title}\n${message}`);
// const bannerImage = "https://placehold.co/1080x1920/1A237E/fff?text=Forgot+Password+Banner";

// Custom theme to match the colors from the login and register pages
const customTheme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#ff9800",
      contrastText: "#fff",
    },
    scraptradeYellow: {
      main: "#F4C542",
      dark: "#E3AE27",
    },
    scraptradeDark: {
      main: "#2D2D2D",
    },
    background: {
      default: "#f4f6f8",
    },
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "8px",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
          paddingTop: "12px",
          paddingBottom: "12px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#f0f4f9",
          borderRadius: "8px",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#F4C542",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E3AE27",
          },
          // New style to target placeholder text on focus
          "&.Mui-focused .MuiInputBase-input::placeholder": {
            color: "#E3AE27", // Use a dark yellow for better contrast
            opacity: 1, // Ensure it is fully visible
          },
          // New style to target the label on focus
          "&.Mui-focused .MuiInputLabel-root": {
            color: "#E3AE27",
          },
        },
        notchedOutline: {
          borderColor: "transparent",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": {
            color: "#E3AE27",
          },
        },
      },
    },
  },
});

// A custom Formik-compatible TextField component
const FormikTextField = ({ label, ...props }) => {
  const [field, meta, helpers] = useField(props);
  const errorText = meta.error && meta.touched ? meta.error : "";
  return (
    <TextField
      {...props}
      {...field}
      label={label}
      fullWidth
      margin="normal"
      variant="outlined"
      error={!!errorText}
      helperText={errorText}
    />
  );
};

const ForgotPasswordSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Required"),
});

const ForgotPassword = () => {
  const isMobile = useMediaQuery(customTheme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // The handleSubmit function now uses a mock API call for a standalone example.
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Simulate an API call
      await dispatch(forgotPassword({ email: values.email })).unwrap();

      showAppMessageToast({
        type: "success",
        title: "Email Sent",
        message: "OTP has been sent to your email. Please check your inbox.",
      });
      navigate("/reset-password", { state: { email: values.email } });
    } catch (err) {
      showAppMessageToast({
        type: "error",
        title: "Failed to Send OTP",
        message:
          typeof err === "string"
            ? err
            : err?.message || "Unable to send OTP. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      {/* Left Banner Section - hidden on mobile */}
      {!isMobile && (
        <Grid
          item
          md={8}
          sx={{
            backgroundImage: `url(${bannerImage})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "500px",
          }}
        >
          {/* Banner Overlay */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.55)",
              zIndex: 1,
            }}
          />
          {/* Banner Text Content */}
          <Box
            sx={{
              zIndex: 2,
              textAlign: "center",
              p: 4,
              minWidth: "786px",
              color: "white",
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              fontWeight={700}
              sx={{ my: 1, fontSize: "2.5rem" }}
            >
              Scrap Trade
            </Typography>
            <Typography variant="body1" sx={{ color: "#ddd" }}>
              Reset your password and get back to trading.
            </Typography>
          </Box>
        </Grid>
      )}
      {/* Right Form Section */}
      <Grid
        item
        xs={12}
        md={4}
        component={Paper}
        elevation={0}
        square
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: customTheme.palette.background.default,
          flexGrow: 1,
          p: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            my: { xs: 4, md: 8 },
            p: 4,
            width: "100%",
            maxWidth: 440,
            borderRadius: "16px",
            boxShadow: "0 0 30px rgba(0,0,0,0.05)",
            backgroundColor: "white",
          }}
        >
          <Typography component="h2" variant="h5" fontWeight={600} mb={3}>
            Forgot Password
          </Typography>
          <Formik
            initialValues={{ email: "" }}
            validationSchema={ForgotPasswordSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form noValidate>
                <FormikTextField
                  name="email"
                  type="email"
                  label="Email"
                  placeholder="Enter your email"
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    mb: 2,
                    fontWeight: 600,
                    backgroundColor: customTheme.palette.scraptradeYellow.main,
                    color: "#2D3A27",
                    "&:hover": {
                      backgroundColor:
                        customTheme.palette.scraptradeYellow.dark,
                    },
                  }}
                  disabled={isSubmitting}
                >
                  Send
                </Button>

                <Divider sx={{ my: 2, borderColor: "#e0e0e0" }} />

                <Button
                  type="button"
                  fullWidth
                  variant="outlined"
                  onClick={handleBackToLogin}
                  sx={{
                    color: customTheme.palette.scraptradeYellow.main,
                    borderColor: customTheme.palette.scraptradeYellow.main,
                    "&:hover": {
                      borderColor: customTheme.palette.scraptradeYellow.dark,
                      backgroundColor:
                        customTheme.palette.scraptradeYellow.main + "1a", // A little transparent background on hover
                    },
                  }}
                >
                  Back to Login
                </Button>
              </Form>
            )}
          </Formik>
        </Box>
      </Grid>
    </Box>
  );
};

export default function App() {
  return (
    <ThemeProvider theme={customTheme}>
      <CssBaseline />
      <ForgotPassword />
    </ThemeProvider>
  );
}
